import { useCallback, useState } from 'react';
import { useStore } from 'react-redux';
import { Text } from '@rsos/base-components';
import Select from '@rsos/base-components/Select';
import { createFetchRetry } from '@rsos/capstone';
import { Button } from '@rsos/components/capstone/base';
import { useFlag } from '@rsos/flags';
import { useSelectedRequestId } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { getAPIHost } from '@rsos/utils/metaTags';
import { STATUS_TYPES } from '../AirAmbulanceForm/constants';

interface StatusState {
  type: 'loading' | 'success' | 'error';
  message: string;
}

const STATUS_TYPE_OPTIONS = Object.values(STATUS_TYPES).map(({ value }) => ({
  label: value,
  value,
}));

/**
 * Test component for ETL integrations endpoints.
 * Only visible when air_ambulance_etl_integrations feature flag is enabled.
 * After the form submits a transport, this panel picks up the tripId from
 * context and allows sending test status updates against it.
 */
export default function AirAmbulanceEtlTest(): JSX.Element | null {
  const isEtlTestEnabled = useFlag('air_ambulance_etl_integrations');
  const orgName = useCurrentOrgName();
  const store = useStore();
  const requestId = useSelectedRequestId();

  const [status, setStatus] = useState<StatusState | null>(null);
  const [selectedOption, setSelectedOption] = useState(STATUS_TYPE_OPTIONS[0]);

  const handleUpdateStatus = useCallback(async () => {
    if (!requestId) {
      setStatus({
        type: 'error',
        message: 'No transport submitted yet. Submit the form first.',
      });
      return;
    }

    setStatus({ type: 'loading', message: 'Updating status...' });
    try {
      const statusType = selectedOption.value;
      const payload = {
        olosSystemTripId: requestId,
        extSystemTripId: requestId,
        isTest: true,
        update: {
          status: {
            type: statusType,
            display: `${selectedOption.label} - ${new Date().toLocaleTimeString()}`,
            statusTimeUTC: new Date().toISOString(),
          },
          vehicle: {
            id: 'TEST-VEHICLE-1',
            name: 'N123AB',
          },
          provider: {
            id: 'TEST-PROVIDER-1',
            name: 'Test Air Medical',
            phoneNumber: '555-0100',
          },
        },
        url: {
          transportTracking: 'https://gmrtransport.net/tracking/12782891...',
        },
      };

      if (!orgName) {
        throw new Error("Current user's organization name not available");
      }

      const fetchRetry = createFetchRetry(store);
      const response = await fetchRetry(
        `${getAPIHost()}/v1/etl-integrations/ingress/gmr/statuses`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-RapidSOS-Source': 'air_ambulance',
            'X-RapidSOS-Org': orgName,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // eslint-disable-next-line no-console
      console.debug('Status Update Response:', data);
      setStatus({
        type: 'success',
        message: `Sent "${selectedOption.label}" status`,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Update Status Error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      setStatus({ type: 'error', message: `Error: ${errorMessage}` });
    }
  }, [requestId, selectedOption, store, orgName]);

  if (!isEtlTestEnabled) {
    return null;
  }

  return (
    <div
      style={{
        padding: 12,
        marginTop: 12,
        border: '2px dashed',
        borderColor: 'rgba(128, 128, 128, 0.4)',
        borderRadius: 4,
      }}
    >
      <Text color="heavyDanger" size="normal" fontWeight="bold">
        DEV/TEST ONLY - ETL Status Simulator
      </Text>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginTop: 8,
        }}
      >
        {requestId ? (
          <Text color="primaryText" size="xsmall">
            Trip ID: {requestId}
          </Text>
        ) : (
          <Text color="secondaryText" size="xsmall">
            Submit the form to create a transport
          </Text>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <div style={{ minWidth: 160 }}>
            <Select
              options={STATUS_TYPE_OPTIONS}
              value={selectedOption}
              onChange={(option: { label: string; value: string } | null) => {
                if (option) {
                  setSelectedOption(option);
                }
              }}
              isClearable={false}
              isSearchable={false}
              placeholder="Status type"
            />
          </div>

          <Button
            btnType="secondary"
            size="small"
            onClick={handleUpdateStatus}
            disabled={status?.type === 'loading' || !requestId}
          >
            Send Status Update
          </Button>
        </div>

        {status && (
          <Text
            color={
              status.type === 'error'
                ? 'heavyDanger'
                : status.type === 'success'
                  ? 'success'
                  : 'secondaryText'
            }
            size="xsmall"
          >
            {status.message}
          </Text>
        )}
      </div>
    </div>
  );
}
